/**
 * SV-20 Stream A: Agent Configuration Registry
 *
 * Single source of truth for all 13 Office agent identities (Sigma orchestrator + 12 grid agents).
 * Extracted from the Option D mockup and use-backend-live.ts AGENT_INFO/NODE_AGENTS maps.
 * Expanded in SV-PORT-004a to 12 grid tiles (4x3 desktop / 3x4 tablet / 2x6 mobile).
 *
 * Downstream consumers: AgentTile, FlowStrip, AgentDrawer, OrchestratorBar.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** All 13 agent persona keys used across the Office surface (1 orchestrator + 12 grid). */
export type AgentKey =
  | "sigma"
  | "carter"
  | "ben-kim"
  | "annalisa"
  | "dollar-bill"
  | "wags"
  | "wendy"
  | "jack-mercer"
  | "devin-matrix"
  | "axe"
  | "reggie"
  | "taylor-mason"
  | "lion";

/** Static metadata for a single agent. */
export interface AgentMetadata {
  /** Unique persona key (kebab-case). */
  key: AgentKey;
  /** Display name shown in tiles and drawer header. */
  name: string;
  /** Short functional role shown below the name. */
  role: string;
  /** Abbreviated name for compact UI (chip strip labels). */
  shortName: string;
  /** One-paragraph profile bio rendered in the drawer Profile tab. */
  bio: string;
  /**
   * SIG-434 (DL-37): purely COSMETIC avatar ring color. The old `model`
   * field carried fabricated model identities ("Claude Opus 4.7", "GPT-4o",
   * "Playwright") — the runtime resolves models per tier/lane through
   * config-routed OpenRouter ids (backend `runtime/model_client.py`
   * MODEL_ROUTES) and no API exposes the resolved id to the frontend, so the
   * registry claims NO model identity at all. If a runtime model-config
   * surface ships, badges may source from it — never from a static string.
   */
  avatarRing: AgentAvatarRing;
  /** Path to approved roster card in /redesign-mockups/assets/. */
  avatarUrl: string;
  /**
   * Pipeline step number (1-9).
   * Sigma uses the special sentinel value to indicate "orchestrator / no step".
   */
  step: number | "\u25CE";
  /** True for agents that bridge between analysis and execution (e.g. Wags). */
  isBridge?: boolean;
  /** True for the orchestrator agent (Sigma). */
  isBoss?: boolean;
  /** Default badge labels shown in the drawer meta row. */
  defaultBadges?: string[];
  /** Key-value attribute pairs rendered in the profile card. */
  attribs: Array<[string, string]>;
}

/** SIG-434: cosmetic ring palette — a color choice, not a capability claim. */
export type AgentAvatarRing = "cyan" | "silver" | "amber";

// ---------------------------------------------------------------------------
// Flow order
// ---------------------------------------------------------------------------

/**
 * Ordered list of agent keys matching the pipeline execution sequence.
 * Sigma (orchestrator) is step 0 / "\u25CE", followed by steps 1-12.
 *
 * SV-PORT-004a: expanded from 10 to 13 agents (sigma + 12 grid tiles).
 */
export const FLOW_ORDER: AgentKey[] = [
  "sigma",
  "reggie",
  "jack-mercer",
  "carter",
  "ben-kim",
  "annalisa",
  "taylor-mason",
  "dollar-bill",
  "wags",
  "wendy",
  "lion",
  "devin-matrix",
  "axe",
];

// ---------------------------------------------------------------------------
// Agent registry
// ---------------------------------------------------------------------------

export const AGENTS_CONFIG: Record<AgentKey, AgentMetadata> = {
  sigma: {
    key: "sigma",
    name: "Sigma",
    role: "Orchestrator",
    shortName: "Sigma",
    bio: "Sigma is the boss \u2014 the run coordinator. Live nodes load episodic memory and select regime-conditional prompts. Wendy owns the go/no-go; there is no sigma_arbitration node.",
    avatarRing: "cyan",
    avatarUrl: "/redesign-mockups/assets/roster-sigma.webp",
    step: "\u25CE",
    isBoss: true,
    // SIG-288: attribs carry identity only — runtime telemetry (uptime,
    // wave progress, concurrency) must come from live run data, never
    // static config claims.
    defaultBadges: ["Boss"],
    attribs: [],
  },

  carter: {
    key: "carter",
    name: "Carter Hayes",
    role: "Strategy Worker",
    shortName: "Carter",
    bio: "Carter is the first-pass strategy analyst. On an attached strategy he runs the profile's entry rules plus bar-derived direction (EMA/VWAP/ATR). Volume profile is not in this node yet.",
    avatarRing: "amber",
    avatarUrl: "/redesign-mockups/assets/roster-carter.webp",
    step: 3,
    attribs: [],
  },

  "ben-kim": {
    key: "ben-kim",
    name: "Ben Kim",
    role: "Signal Filter",
    shortName: "Ben Kim",
    bio: "Ben Kim is the visual confirmation layer. Captures and analyzes the chart, then scores signals across multiple timeframes. Blocks weak signals from reaching execution.",
    avatarRing: "amber",
    avatarUrl: "/redesign-mockups/assets/roster-benkim.webp",
    step: 4,
    attribs: [["Timeframes", "5m / 15m / 1h"]],
  },

  annalisa: {
    key: "annalisa",
    name: "Annalisa",
    role: "News & Sentiment",
    shortName: "Annalisa",
    bio: "Annalisa is the macro overlay plus the early structural event scan that feeds Carter. Headlines/calendar apply when a feed is present. She proposes news-blackout windows; she does not arm them.",
    avatarRing: "silver",
    avatarUrl: "/redesign-mockups/assets/roster-annalisa.webp",
    step: 5,
    attribs: [],
  },

  "dollar-bill": {
    key: "dollar-bill",
    name: "Dollar Bill",
    role: "Risk Manager",
    shortName: "Dollar Bill",
    bio: "Dollar Bill enforces the capital preservation layer. Sizes from daily-loss ceiling, open positions, and drawdown. A reject here is stamped for Wags/Wendy — he is not the last node before the broker.",
    avatarRing: "cyan",
    avatarUrl: "/redesign-mockups/assets/roster-dollarbill.webp",
    step: 7,
    attribs: [],
  },

  wags: {
    key: "wags",
    name: "Wags",
    role: "Compliance & Ops",
    shortName: "Wags",
    bio: "Wags is the prop-firm rules bridge. Validates each order packet against the active firm rule set (daily loss, trailing drawdown, trading hours, position limits). News-policy flags are recorded; they are not yet a Wags veto.",
    avatarRing: "cyan",
    avatarUrl: "/redesign-mockups/assets/roster-wags.webp",
    step: 8,
    isBridge: true,
    defaultBadges: ["Bridge"],
    attribs: [],
  },

  wendy: {
    key: "wendy",
    name: "Wendy",
    role: "Synthesis",
    shortName: "Wendy",
    bio: "Wendy is the synthesis layer. Combines Carter, Ben Kim, Annalisa, risk, and compliance into a final trade proposal (approve / human_review / reject). Lion owns the HITL gate.",
    avatarRing: "cyan",
    avatarUrl: "/redesign-mockups/assets/roster-wendy.webp",
    step: 9,
    defaultBadges: ["Synthesis"],
    attribs: [],
  },

  "devin-matrix": {
    key: "devin-matrix",
    name: "Devin Matrix",
    role: "Execution Routing",
    shortName: "Devin",
    bio: "Devin picks the configured execution path and routes every order through an available broker API. Browser-exec is a retired ghost node — it is not a live fallback.",
    avatarRing: "amber",
    avatarUrl: "/redesign-mockups/assets/roster-devinmatrix.webp",
    step: 11,
    attribs: [],
  },

  axe: {
    key: "axe",
    name: "Axe",
    role: "P&L Snapshot",
    shortName: "Axe",
    bio: "Axe closes every run. Snapshots P&L, writes the journal entry, archives screenshots, and ships the trade report to the learning loop for future prompt tuning.",
    avatarRing: "amber",
    avatarUrl: "/redesign-mockups/assets/roster-axe.webp",
    step: 12,
    defaultBadges: ["Journaler"],
    attribs: [],
  },

  // SV-PORT-004a: New agents to complete 12-tile grid
  reggie: {
    key: "reggie",
    name: "Reggie",
    role: "Regime Detection",
    shortName: "Reggie",
    bio: "Reggie classifies the current market regime from a 20-bar OHLCV window (ATR vs price + net-move trend). Advisory only — Wendy may nudge confidence on trend_up/trend_down. Volume profile, VIX, and clustering are not in this node yet.",
    avatarRing: "silver",
    avatarUrl: "/redesign-mockups/assets/roster-reggie.webp",
    step: 1,
    defaultBadges: ["Regime"],
    attribs: [],
  },

  "jack-mercer": {
    key: "jack-mercer",
    name: "Jack Mercer",
    role: "Order Flow",
    shortName: "Jack Mercer",
    bio: "Jack Mercer confirms setups with bar-derived order-flow imbalance (OFI): buy/sell pressure, imbalance z-score, and absorption proxies from OHLCV — not a live time-and-sales tape until that lane ships.",
    avatarRing: "silver",
    avatarUrl: "/redesign-mockups/assets/roster-jackmercer.webp",
    step: 2,
    defaultBadges: ["Order Flow"],
    attribs: [],
  },

  "taylor-mason": {
    key: "taylor-mason",
    name: "Taylor Mason",
    role: "The Arena (Bull/Bear)",
    shortName: "Taylor",
    bio: "Taylor Mason runs The Arena — the bull-vs-bear debate. Argues both sides of every setup, stress-tests the thesis, and surfaces the strongest case for and against before synthesis decides. The debate transcript is the most watchable artifact on the floor.",
    avatarRing: "cyan",
    avatarUrl: "/redesign-mockups/assets/roster-taylor.webp",
    step: 6,
    defaultBadges: ["Debate"],
    attribs: [
      ["Stance", "Bull vs Bear"],
      ["Verdict", "to Wendy"],
    ],
  },

  lion: {
    key: "lion",
    name: "Lion",
    role: "HITL Gatekeeper",
    shortName: "Lion",
    bio: "Lion is the human-in-the-loop approval gate. He holds every proposed trade at the breakpoint and waits for your sign-off before Devin Matrix routes it to a broker. Configurable per account — full auto, ask-on-large, ask-on-novel, or ask-on-everything — pushing to your phone and deferring to Wags' standing rules if you don't respond in time.",
    avatarRing: "amber",
    avatarUrl: "/redesign-mockups/assets/roster-lion.webp",
    step: 10,
    defaultBadges: ["HITL"],
    attribs: [],
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Look up agent metadata by key. */
export function getAgentByKey(key: AgentKey): AgentMetadata {
  return AGENTS_CONFIG[key];
}

// ---------------------------------------------------------------------------
// Backend node-key mapping
// ---------------------------------------------------------------------------

/**
 * SIG-712: ghost node ids with no graph file / no `add_node` registration.
 * Kept OUT of NODE_KEY_MAP so the floor never lights a persona for vaporware.
 * Historical aliases (Opportunity Scout, SR-Tape, browser exec) stay here only.
 */
export const DEPRECATED_GHOST_NODE_KEYS: ReadonlySet<string> = new Set([
  "sigma_orchestrator",
  "sigma_arbitration",
  "tape_reader",
  "exec_browser",
  "opportunity_scout",
  "scanner",
]);

/**
 * Maps backend LangGraph node IDs to front-end AgentKey.
 *
 * SSOT for Office floor attribution. use-backend-live must resolve through
 * `normalizeAgentKey` / `nodeKeyToAgentKey` — do not fork a second map there.
 * Multiple real nodes can map to the same agent (e.g. exec_api + exec_router →
 * devin-matrix). Ghosts listed in DEPRECATED_GHOST_NODE_KEYS are intentionally
 * absent.
 */
const NODE_KEY_MAP: Record<string, AgentKey> = {
  // SIG-469: Operations-family nodes that emitted events with NO AgentKey —
  // the floor silently dropped them. memory_context assembles run context and
  // prompt_selector dispatches the regime-tuned prompt set to the reasoning
  // agents: both are the orchestrator's prep work, so they light the Sigma
  // tile. Each node maps under BOTH its graph registration id (add_node key)
  // and its NODE_NAME event vocabulary.
  memory_context_node: "sigma",
  memory_context: "sigma",
  prompt_selector_node: "sigma",
  prompt_selector: "sigma",
  // SIG-469: event_discovery scans the macro/event calendar — that is
  // Annalisa's News & Sentiment beat.
  event_discovery_node: "annalisa",
  event_discovery: "annalisa",
  // SIG-469: copy_trade mirrors the leader's order to follower accounts —
  // execution-family work, so it lights Devin Matrix (Execution Routing).
  copy_trade_node: "devin-matrix",
  copy_trade: "devin-matrix",
  // SV-PORT-004a / SV-44: New agent nodes.
  // The backend broadcasts the graph node ids regime_node / order_flow_node
  // (each node's NODE_NAME); the *_node literals are what the Office floor
  // actually receives. The suffixless keys stay for any legacy event source.
  regime_node: "reggie",
  regime_detection: "reggie",
  order_flow: "jack-mercer",
  order_flow_node: "jack-mercer",
  // Pipeline nodes. SIG-469: each persona maps under BOTH the office/NODE_NAME
  // vocabulary (e.g. "strategy_worker", what flow_service._OFFICE_NODE_ALIASES
  // canonicalizes to) AND the raw graph registration id ("strategy_worker_node",
  // the add_node key) — event paths that skip the backend alias table
  // (transcripts, raw LangGraph streams) broadcast the registration id.
  strategy_worker: "carter",
  strategy_worker_node: "carter",
  signal_filter: "ben-kim",
  signal_filter_node: "ben-kim",
  sentiment_node: "annalisa",
  risk_node: "dollar-bill",
  compliance_node: "wags",
  compliance: "wags",
  synthesis: "wendy",
  synthesis_node: "wendy",
  hitl_breakpoint: "lion",
  debate: "taylor-mason",
  debate_node: "taylor-mason",
  hitl_node: "lion",
  exec_router: "devin-matrix",
  exec_router_node: "devin-matrix",
  exec_api: "devin-matrix",
  exec_api_node: "devin-matrix",
  pnl_snapshot: "axe",
  pnl_snapshot_node: "axe",
};

/**
 * SIG-469: trading-graph nodes DELIBERATELY hidden from the Office floor.
 *
 * These are bookkeeping/terminal plumbing with no persona: rendering them as
 * agent activity would fabricate roster work that no agent performed. Every
 * node registered in `trading_graph.py` must appear either in NODE_KEY_MAP or
 * here — pinned by tests/office-node-key-map-coverage.test.ts, so the NEXT
 * new backend node fails that contract test instead of dying silently.
 */
export const HIDDEN_SINK_NODES: ReadonlySet<string> = new Set([
  // Signal persistence/rejection sinks: they write pipeline outcomes to the
  // DB after the deciding agents already rendered their verdicts on-floor.
  "pipeline_signal_persistence_node",
  "pipeline_signal_rejection_node",
  // Terminal handlers: graph exits (rejected / execution halted). The
  // rejection/halt itself is surfaced by the node that decided it (risk,
  // compliance, exec) — these sinks only close out the run.
  "end_rejected",
  "end_execution_halted",
]);

/**
 * Convert a backend node key (e.g. "signal_filter") to the front-end AgentKey.
 * Returns null for unknown node keys (including DEPRECATED_GHOST_NODE_KEYS).
 */
export function nodeKeyToAgentKey(nodeKey: string): AgentKey | null {
  return NODE_KEY_MAP[nodeKey] ?? null;
}

/**
 * Resolve a persona from a backend `agent_key` and/or graph `node_id`.
 * Accepts kebab-case AgentKey, snake_case variants, and the legacy `devin`
 * shorthand. Ghosts resolve to null.
 */
export function normalizeAgentKey(
  agentKey?: string | null,
  nodeId?: string | null,
): AgentKey | null {
  if (agentKey) {
    if (Object.prototype.hasOwnProperty.call(AGENTS_CONFIG, agentKey)) {
      return agentKey as AgentKey;
    }
    const kebab = agentKey.replace(/_/g, "-");
    if (Object.prototype.hasOwnProperty.call(AGENTS_CONFIG, kebab)) {
      return kebab as AgentKey;
    }
    if (agentKey === "devin" || kebab === "devin") {
      return "devin-matrix";
    }
  }
  if (nodeId) {
    return nodeKeyToAgentKey(nodeId);
  }
  return null;
}
